<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12" md="8" class="text-justify">
        <h2 class="display-2 text-center mb-4 text-secondary">About the Holonet Galactic Encyclopedia</h2>

        <p class="text-body-1">
          The Holonet Galactic Encyclopedia is a project by
          <a href="https://yoanchabot.github.io" target="_blank">Yoan Chabot</a>, an AI researcher passionate about Star
          Wars and linked data.
          This project aims to bridge the gap between Wookieepedia’s rich lore and structured, queryable data, creating
          a seamless way to explore the galaxy.
          When I started this project, there were only 47 planets represented in Wikidata. Motivated by my passion for
          both Star Wars and the potential of linked data, I decided to enrich the knowledge graph drastically,
          expanding the representation of planets, regions, and the entire Star Wars universe to make the galaxy more
          accessible and discoverable for everyone.
        </p>

        <h3 class="mt-5 text-secondary">1. Why Wikidata?</h3>
        <p class="text-body-1">
          This project involves enriching Wikidata with a wealth of Star Wars information from Wookieepedia.
          Wikidata is a powerful, open knowledge base that allows data to be linked and queried in ways that traditional
          resources like wikis cannot provide.
          Here are some of the benefits of using Wikidata:
        </p>
        <ul class="pl-6">
          <li>Data is structured, which makes it easier to query, analyze, and connect different entities across the
            galaxy.</li>
          <li>Linking entities like planets, characters, and events to create a web of interrelated data, enabling
            powerful queries and deeper exploration.</li>
          <li>Performing advanced SPARQL queries to uncover hidden connections.</li>
          <li>Providing open, queryable, and multilingual data to fans and researchers alike.</li>
          <li>The open nature of Wikidata allows anyone to edit and contribute, fostering a community-driven effort to
            enrich the Star Wars knowledge base.</li>
        </ul>


        <h3 class="mt-5 text-secondary">2. Data Integration with Wikidata: The Role of the WikiProject Star Wars</h3>
        <p class="text-body-1">
          The Holonet Galactic Encyclopedia is not just about collecting information; it’s about enhancing and
          connecting data to create an interactive experience.
          The more data we add to Wikidata, the more robust the project becomes.
          The <a href="https://m.wikidata.org/wiki/Wikidata:WikiProject_Star_Wars" target="_blank">WikiProject Star
            Wars</a> is a collaborative initiative aimed at improving and structuring the data related to the Star Wars
          universe on Wikidata.
          By contributing to this project, we ensure that the Star Wars galaxy is well-represented and that the data is
          rich, accurate, and accessible to all.
        </p>

        <p class="text-body-1">
          Some of the key goals of the WikiProject Star Wars include:
        </p>
        <ul class="text-body-1 pl-6">
          <li>Standardizing the representation of Star Wars entities such as characters, planets, vehicles, and species.
          </li>
          <li>Adding references to Wookieepedia and other reliable sources to ensure the accuracy and completeness of
            data.</li>
          <li>Linking Star Wars entities to relevant concepts in Wikidata, such as "instance of," "part of," "associated
            with," etc., to build a richer, interconnected dataset.</li>
          <li>Maintaining the consistency of labels, descriptions, and claims across various languages to foster a
            global, multilingual resource.</li>
        </ul>

        <p class="text-body-1">
          This project directly contributes to the WikiProject Star Wars by linking Star Wars data between Wookieepedia
          and Wikidata, ensuring a deeper integration and offering a more connected and detailed view of the galaxy. You
          can help by participating in this initiative, whether by adding data or improving the links between
          Wookieepedia and Wikidata.
        </p>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { useHead } from '@unhead/vue';

export default {
  name: 'About',
  setup() {
    useHead({
      title: "About Holonet – Star Wars Encyclopedia with Wikidata",
      meta: [
        {
          name: "description",
          content: "Holonet Galactic Encyclopedia merges Wookieepedia’s Star Wars lore with Wikidata, providing structured, queryable data for fans and researchers. Discover the ultimate galaxy knowledge base.",
        },
        {
          property: "og:title",
          content: "About Holonet – Star Wars Encyclopedia with Wikidata",
        },
        {
          property: "og:description",
          content: "Holonet bridges Wookieepedia and Wikidata to create a structured, queryable Star Wars knowledge base. Learn about the project’s mission and data sources.",
        },
        {
          property: "og:url",
          content: "https://holonetgalacticmap-frontend.vercel.app/about",
        },
        { property: "og:type", content: "website" },
        {
          name: "twitter:title",
          content: "About Holonet – Star Wars Encyclopedia with Wikidata",
        },
        {
          name: "twitter:description",
          content: "Holonet combines Wookieepedia and Wikidata to expand structured Star Wars knowledge. A resource for fans and researchers.",
        },
        { name: "twitter:card", content: "summary" },
        {
          name: "keywords",
          content: "Holonet, Star Wars encyclopedia, Wookieepedia, Wikidata, Star Wars lore, structured data, Star Wars knowledge base, galactic data",
        },
      ],
    });
  }
};
</script>
